// Helpers
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

var Shopify = Shopify || {};

// ---------------------------------------------------------------------------
// Money format handler
// ---------------------------------------------------------------------------
Shopify.money_format = '{{amount}}€';
Shopify.formatMoney = function (cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt === 'undefined' ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ' ');
    decimal = defaultOption(decimal, ',');

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';

    return dollars + cents;
  }

  switch (formatString) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return value;
};

// CartManager
class CartManager {
  constructor() {
    const CART_REQUEST_TYPES = {
      ADD: 'add',
      UPDATE: 'update',
      CHANGE: 'change',
      REMOVE: 'remove',
      CLEAR: 'clear',
    };

    this.cart_request_type = CART_REQUEST_TYPES;
    this.requestsQueue = [];
  }

  async getCart() {
    return await this.fetchCart();
  }

  async updateNote(note, options = {}) {
    const formData = { note };
    return this.postShopify(formData, this.cart_request_type.UPDATE, {
      onSuccess: options.onSuccess || function () {},
      onFailure: options.onFailure || function () {},
      always: options.always || function () {},
    });
  }

  handleErrors(error) {
    const errorMessage = `${error.status || ''} ${error.message || ''} ${error.description || ''}`;
    console.error(errorMessage);
    return errorMessage;
  }

  addItem(variant_id, quantity, properties, options) {
    const formData = {
      items: [
        {
          id: variant_id,
          quantity,
          properties,
        },
      ],
    };
    this.postShopify(formData, this.cart_request_type.ADD, options);
  }

  updateItems(requests, onAllExecuted) {
    if (!Array.isArray(requests)) requests = [requests];
    const shopifyRequestsList = [];
    const shopifyUpdateRequest = { updates: {} };
    const shopifyAddRequest = { items: [] };

    for (let request of requests) {
      const action = request.type;
      const items = request.items;
      if (action === this.cart_request_type.ADD) {
        for (let item of items) {
          if (!item.id || isNaN(item.quantity)) {
            console.error('To add a new item both properties: ID & QUANTITY are required', item);
            continue;
          }

          const cartItem = {
            id: item.id,
            quantity: item.quantity,
            properties: item.properties || {},
          };
          shopifyAddRequest.items.push(cartItem);
        }
      } else if (action === this.cart_request_type.UPDATE) {
        for (let item of items) {
          if (!item.key || isNaN(item.quantity)) {
            console.error('To update an existing item, both properties: KEY & QUANTITY are required', item);
            continue;
          }

          const cartItem = {
            id: item.key,
            quantity: item.quantity,
            properties: item.properties || {},
          };

          if (Object.keys(cartItem.properties).length > 0) {
            shopifyRequestsList.push({
              type: this.cart_request_type.CHANGE,
              body: cartItem,
            });
          } else {
            shopifyUpdateRequest.updates[cartItem.id] = cartItem.quantity;
          }
        }
      } else if (action === this.cart_request_type.REMOVE) {
        for (let item of items) {
          if (!item.key) {
            console.error('To remove an existing item, KEY is required', item);
            continue;
          }
          shopifyUpdateRequest.updates[item.key] = 0;
        }
      }
    }

    if (Object.keys(shopifyUpdateRequest.updates).length) {
      shopifyRequestsList.push({
        type: this.cart_request_type.UPDATE,
        body: shopifyUpdateRequest,
      });
    }
    if (shopifyAddRequest.items.length) {
      shopifyRequestsList.push({
        type: this.cart_request_type.ADD,
        body: shopifyAddRequest,
      });
    }

    let requestsCount = shopifyRequestsList.length;
    const onFinished = () => {
      requestsCount--;
      if (requestsCount === 0 && typeof onAllExecuted === 'function') {
        onAllExecuted();
      }
    };

    for (let shopifyRequest of shopifyRequestsList) {
      this.postShopify(shopifyRequest.body, shopifyRequest.type, {
        always: onFinished,
      });
    }
  }

  clearCart() {
    const formData = {};
    this.postShopify(formData, this.cart_request_type.CLEAR);
  }

  postShopify(formData, action, options = {}) {
    let url = '';

    options.onSuccess = options.onSuccess || function () {};
    options.onFailure = options.onFailure || function () {};
    options.always = options.always || function () {};

    switch (action) {
      case this.cart_request_type.ADD:
        url = window.routes.cartAddJS;
        break;
      case this.cart_request_type.UPDATE:
        url = window.routes.cartUpdateJS;
        break;
      case this.cart_request_type.CHANGE:
        url = window.routes.cartChangeJS;
        break;
      case this.cart_request_type.CLEAR:
        url = window.routes.cartClearJS;
        break;
      default:
        console.error(`Can't post to Shopify. The action: ${action} is not valid`);
        return;
    }

    // Add request to queue
    this.requestsQueue.push({ formData, action, options });

    // If only one request in queue, process it
    if (this.requestsQueue.length === 1) {
      this.processQueue();
    }
  }

  processQueue() {
    if (this.requestsQueue.length === 0) return;

    const { formData, action, options } = this.requestsQueue[0];

    let url = '';

    switch (action) {
      case this.cart_request_type.ADD:
        url = window.routes.cartAddJS;
        break;
      case this.cart_request_type.UPDATE:
        url = window.routes.cartUpdateJS;
        break;
      case this.cart_request_type.CHANGE:
        url = window.routes.cartChangeJS;
        break;
      case this.cart_request_type.CLEAR:
        url = window.routes.cartClearJS;
        break;
      default:
        console.error(`Can't post to Shopify. The action: ${action} is not valid`);
        this.requestsQueue.shift();
        this.processQueue();
        return;
    }

    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (response.ok) return response.json();
        throw Error(response.statusText);
      })
      .then((cart) => {
        options.onSuccess(cart);
      })
      .catch((error) => {
        this.handleErrors(error);
        options.onFailure(error);
      })
      .finally(() => {
        options.always();
        // Remove the processed request from the queue and process the next one
        this.requestsQueue.shift();
        this.processQueue();
      });
  }

  async fetchCart() {
    const response = await fetch(window.routes.cartJSON);
    return await response.json();
  }
}

const cartManager = new CartManager();

// Remove Button
customElements.define(
  'cart-remove-button',
  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();

      this.removeEvent = new Event('remove', { bubbles: true });
      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.dispatchEvent(this.removeEvent);
      });
    }
  }
);

// Cart Items
customElements.define(
  'cart-items',
  class CartItems extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        cartNotification: 'cart-notification',
        variantSelects: 'variant-selects',
      };

      this.cartNotifications = document.querySelectorAll(this.selectors.cartNotification);
      this.variantSelects = document.querySelectorAll(this.selectors.variantSelects);

      this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value),
        0
      );

      this.debouncedOnChange = debounce(this.onChange.bind(this), 300);

      this.addEventListener('change', this.debouncedOnChange);

      this.addEventListener('remove', async (event) => {
        const removeGift = event.target.dataset?.removeGift ? this.removeGift.bind(this) : null;
        const removePack = event.target.dataset?.hasPack === 'true';
        if (removePack) {
          const packId = event.target.dataset?.packId;
          await this.updateQuantityPack(
            event.target.dataset.index,
            0,
            document.activeElement?.getAttribute('name'),
            packId
          );
        } else {
          this.updateQuantity(event.target.dataset.index, 0, document.activeElement?.getAttribute('name'), removeGift);
        }
      });
    }

		static get observedAttributes() {
			return [
				'data-need-to-special-products-delete',
				'data-special-keys',
				'data-use-security-check-zax',
			];
		}

		attributeChangedCallback(name, oldValue, newValue) {
			if (oldValue === newValue) return;

			if (
				name === 'data-use-security-check-zax'
			) {
				this.useSecurityCheckZax = this.dataset.useSecurityCheckZax === 'true';

				if (this.useSecurityCheckZax) {
					this.checkCartConditions();
				}
			}
		}

		connectedCallback() {
			this.useSecurityCheckZax = this.dataset.useSecurityCheckZax === 'true';
			this.handleCartUpdateBound = this.handleCartUpdate.bind(this);
			document.body.addEventListener('cart:update', this.handleCartUpdateBound);
			this.initializeCartValidation();
		}

		disconnectedCallback() {
			if (this.handleCartUpdateBound) {
				document.body.removeEventListener('cart:update', this.handleCartUpdateBound);
			}
		}

		async initializeCartValidation() {
			try {
				if (this.useSecurityCheckZax) {
					await this.checkCartConditions();
				}
				// Setup subscription for cart updates
				document.body.addEventListener('cart:update', async (event) => {
					if (
						event?.detail?.cart?.items &&
						(this.useSecurityCheckZax)
					) {
						await this.checkCartConditions(event.detail.cart.items);
					}
				});
			} catch (error) {
				console.error('Failed to initialize cart validation:', error);
			}
		}

		async checkCartConditions(items = null) {
			try {
				const cart = this.cartJSON || (await cartManager.getCart());
				const cartItems = items || cart?.items;
				if (!cartItems?.length) return;

				if (this.useSecurityCheckZax) {
					await this.removeSpecialProductsZax(cart);
				}
			} catch (error) {
				console.error('Error checking cart conditions:', error);
			}
		}

		async removeSpecialProductsZax(cart) {
			try {
				let hasUpdates = false;
				const updates = {};

				// Check for items with the specified property set to 'true'
				const giftItems = cart.items.filter(
					(item) => item.properties && item.properties._zax_add_with_code_discount === 'true',
				);

				// If no gift items found, return
				if (!giftItems.length) return;

				// Process each gift item
				for (const giftItem of giftItems) {
					const discountCodeId = giftItem.properties._zax_add_with_code;

					if (!discountCodeId) continue;

					const matchingDiscounts = cart.cart_level_discount_applications.filter(
						(discount) =>
							(discount.discount_application?.title &&
								discount.discount_application?.title === discountCodeId) ||
							(discount?.title && discount?.title === discountCodeId),
					);

					if (matchingDiscounts.length === 0) {
						updates[giftItem.key] = 0;
						hasUpdates = true;
					}
				}

				if (hasUpdates) {
					await this.fetchCartUpdate(
						updates,
						true,
						document.activeElement?.getAttribute('name') || null,
						null,
					);
				}
			} catch (error) {
				console.error('Error removing special products:', error);
			}
		}

		async fetchCartUpdate(updates, refreshContent, name, line) {
			const body = JSON.stringify({
				updates,
				sections: this.getSectionsToRender()
					.map((section) => section.section)
					.filter(Boolean),
				sections_url: window.location.pathname,
			});

			await fetch(window.routes.cart_update_url, { ...fetchConfig(), body })
				.then((response) => response.text())
				.then((state) => {
					const parsedState = JSON.parse(state);
					this.classList.toggle('is-empty', parsedState.item_count === 0);
					const cartFooter = document.getElementById('main-cart-footer');

					if (cartFooter) {
						cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
					}

					this.cartNotifications.forEach((section) => {
						if (parsedState.item_count) {
							section.updateItemsCount(parsedState.item_count);
						}
					});

					this.variantSelects.forEach((section) => {
						section.reset();
					});

					if (refreshContent) {
						this.getSectionsToRender().forEach((section) => {
							if (section.section != null) {
								const elementToReplace =
									document.getElementById(section.id)?.querySelector(section.selector) ||
									document.getElementById(section.id);
								if (elementToReplace != null) {
									elementToReplace.innerHTML = this.getSectionInnerHTML(
										parsedState.sections[section.section],
										section.selector,
									);
								}
							}
						});

						const dom = new DOMParser().parseFromString(
							parsedState.sections['cart-drawer-v2'],
							'text/html',
						);
						const mainCartItems = dom?.querySelector('#main-cart-items');
						if (mainCartItems) {
							const cartItemsElement = document.querySelector('#main-cart-items');
							if (cartItemsElement) {
								cartItemsElement.dataset.useSecurityCheckZax =
									mainCartItems.dataset.useSecurityCheckZax;
							}
						}
					}

					const lineItem = document.getElementById(`CartItem-${line}`);
					if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
						lineItem.querySelector(`[name="${name}"]`).focus();
					}
					this.disableLoading(line);

					if (typeof callback === 'function') {
            callback();
          }
				})
				.catch(() => {
					this.querySelectorAll('.loading-overlay').forEach((overlay) =>
						overlay.classList.add('hidden'),
					);
					this.disableLoading(line);
				})
		}

		handleCartUpdate(event) {
			this.cartJSON = event.detail?.cart || null;
		}

    removeGift() {
      // Implement the removeGift logic if necessary
    }

    onChange(event) {
      this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement?.getAttribute('name'));
    }

    getSectionsToRender() {
      return [
        {
          id: 'main-cart-items',
          section: document.getElementById('main-cart-items')?.dataset.id || null,
          selector: '[data-items-cart]',
        },
        {
          id: 'cart-drawer-reward',
          section: document.getElementById('cart-drawer-reward')?.dataset.id || null,
          selector: '[data-cart-drawer-reward]',
        },
        {
          id: 'cart-drawer-samples',
          section: document.getElementById('cart-drawer-samples')?.dataset.id || null,
          selector: '[data-cart-drawer-samples]',
        },
        {
          id: 'main-cart-summary',
          section: document.getElementById('main-cart-summary')?.dataset.id || null,
          selector: '.cart-drawer-summary',
        },
        {
          id: 'drawer-cart-footer',
          section: document.getElementById('drawer-cart-footer')?.dataset.id || null,
          selector: '[data-cart-total-price]',
        },
        {
          id: 'drawer-cart-upsell',
          section: document.getElementById('drawer-cart-upsell')?.dataset.id || null,
          selector: '[data-upsell-cart-drawer]',
        },
        {
          id: 'drawer-cart-upsell-dynamic',
          section: document.getElementById('drawer-cart-upsell-dynamic')?.dataset.id || null,
          selector: '[data-upsell-dynamic-cart-drawer]',
        },
      ];
    }

    async updateQuantityPack(line, quantity, name, packId) {
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        mainCartItems.classList.add('cart__items--disabled');
      }

      this.querySelectorAll(`.cart-item__remove-container[data-pack-id="${packId}"]`).forEach((button) => {
        const container = button.closest('.cart-drawer_item-container');
        container?.classList.add('updating');
        container?.querySelectorAll('.loading-overlay').forEach((overlay) => overlay?.classList.remove('hidden'));
      });

      document.activeElement?.blur();
      
      // Vérifier et bloquer le bouton de checkout pendant la mise à jour du pack
      this.checkAndToggleCheckoutButton();

      const cartJson = await cartManager.getCart();

      const currentLine = cartJson.items[line - 1];

      localStorage.setItem('productUpdated', true);

      const itemsWithSameId = cartJson.items.filter((item) => {
        return item.properties['_pack_id'] === currentLine.properties['_pack_id'];
      });

      let updates = {};

      itemsWithSameId.forEach((item) => {
        updates[item.key] = quantity;
      });

      updates[currentLine.key] = quantity;

      const body = JSON.stringify({
        updates,
        sections: this.getSectionsToRender()
          .map((section) => section.section)
          .filter(Boolean),
        sections_url: window.location.pathname,
      });

      fetch('/cart/update', { ...fetchConfig(), body })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) {
            cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          }

          this.cartNotifications.forEach((section) => {
            section.updateItemsCount(parsedState.item_count);
          });

          this.variantSelects.forEach((section) => {
            section.reset();
          });

          this.getSectionsToRender().forEach((section) => {
            if (section.section != null) {
              const elementToReplace =
                document.getElementById(section.id)?.querySelector(section.selector) ||
                document.getElementById(section.id);
              if (elementToReplace != null) {
                elementToReplace.innerHTML = this.getSectionInnerHTML(
                  parsedState.sections[section.section],
                  section.selector
                );
              }
            }
          });

					const dom = new DOMParser().parseFromString(
						parsedState.sections['cart-drawer-v2'],
						'text/html',
					);
					const mainCartItems = dom?.querySelector('#main-cart-items');
					if (mainCartItems) {
						const cartItemsElement = document.querySelector('#main-cart-items');
						if (cartItemsElement) {
							cartItemsElement.dataset.useSecurityCheckZax =
								mainCartItems.dataset.useSecurityCheckZax;
						}
					}

          const lineItem = document.getElementById(`CartItem-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            lineItem.querySelector(`[name="${name}"]`).focus();
          }
          this.disableLoading(line);

          if (typeof callback === 'function') {
            callback();
          }
        })
        .catch(() => {
          this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
          this.disableLoading(line);
          // Vérifier et débloquer le bouton de checkout après l'erreur
          this.checkAndToggleCheckoutButton();
        });
    }

    updateQuantity(line, quantity, name, callback) {
      this.enableLoading(line);

      localStorage.setItem('productUpdated', true);

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender()
          .map((section) => section.section)
          .filter(Boolean),
        sections_url: window.location.pathname,
      });

      fetch('/cart/change', { ...fetchConfig(), body })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) {
            cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          }

          this.cartNotifications.forEach((section) => {
            section.updateItemsCount(parsedState.item_count);
          });

          this.variantSelects.forEach((section) => {
            section.reset();
          });

          this.getSectionsToRender().forEach((section) => {
            if (section.section != null) {
              const elementToReplace =
                document.getElementById(section.id)?.querySelector(section.selector) ||
                document.getElementById(section.id);
              if (elementToReplace != null) {
                elementToReplace.innerHTML = this.getSectionInnerHTML(
                  parsedState.sections[section.section],
                  section.selector
                );
              }
            }
          });

					const dom = new DOMParser().parseFromString(
						parsedState.sections['cart-drawer-v2'],
						'text/html',
					);
					const mainCartItems = dom?.querySelector('#main-cart-items');
					if (mainCartItems) {
						const cartItemsElement = document.querySelector('#main-cart-items');
						if (cartItemsElement) {
							cartItemsElement.dataset.useSecurityCheckZax =
								mainCartItems.dataset.useSecurityCheckZax;
						}
					}

          const lineItem = document.getElementById(`CartItem-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            lineItem.querySelector(`[name="${name}"]`).focus();
          }
          this.disableLoading(line);

          if (typeof callback === 'function') {
            callback();
          }
        })
        .catch(() => {
          this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
          this.disableLoading(line);
          // Vérifier et débloquer le bouton de checkout après l'erreur
          this.checkAndToggleCheckoutButton();
        });
    }

    getSectionInnerHTML(html, selector) {
      const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return element ? element.innerHTML : '';
    }

    /**
     * Vérifie si des loading-overlays sont visibles et active/désactive le bouton de checkout
     * Pour des raisons de sécurité, le bouton checkout doit être bloqué pendant les mises à jour
     */
    checkAndToggleCheckoutButton() {
      const cartItems = this.querySelectorAll('.cart-drawer_item-container');
      let hasVisibleLoading = false;

      // Vérifier si au moins un loading-overlay est visible (n'a pas la classe 'hidden')
      cartItems.forEach((item) => {
        const loadingOverlay = item.querySelector('.loading-overlay');
        if (loadingOverlay && !loadingOverlay.classList.contains('hidden')) {
          hasVisibleLoading = true;
        }
      });

      // Sélectionner les boutons de checkout
      const checkoutButtons = document.querySelectorAll('.cart-drawer_footer-checkout-button');
      const checkoutPriceElements = document.querySelectorAll('.cart-drawer_footer-checkout-button-price');

      // Bloquer ou débloquer les boutons selon l'état des loading-overlays
      checkoutButtons.forEach((button) => {
        if (hasVisibleLoading) {
          button.disabled = true;
          button.style.opacity = '0.5';
          button.style.cursor = 'not-allowed';
          button.style.pointerEvents = 'none';
        } else {
          button.disabled = false;
          button.style.opacity = '';
          button.style.cursor = '';
          button.style.pointerEvents = '';
        }
      });

      checkoutPriceElements.forEach((element) => {
        if (hasVisibleLoading) {
          element.style.opacity = '0.5';
          element.style.pointerEvents = 'none';
        } else {
          element.style.opacity = '';
          element.style.pointerEvents = '';
        }
      });
    }

    enableLoading(line) {
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        mainCartItems.classList.add('cart__items--disabled');
      }
      this.querySelectorAll(`#CartItem-${line} .loading-overlay`).forEach((overlay) =>
        overlay.classList.remove('hidden')
      );
      const cartItem = this.querySelector(`#CartItem-${line}`);
      if (cartItem) {
        cartItem.classList.add('updating');
      }
      document.activeElement?.blur();
      
      // Vérifier et bloquer le bouton de checkout si nécessaire
      this.checkAndToggleCheckoutButton();
    }

    disableLoading(line) {
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        mainCartItems.classList.remove('cart__items--disabled');
      }
      const cartItem = this.querySelector(`#CartItem-${line}`);
      if (cartItem) {
        cartItem.classList.remove('updating');
      }
      
      // Vérifier et débloquer le bouton de checkout si possible
      this.checkAndToggleCheckoutButton();
    }
  }
);

// Quantity Input
customElements.define(
  'cart-quantity-input',
  class CartQuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });

      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    onButtonClick(event) {
      event.preventDefault();
      const previousValue = this.input.value;

      if (event.target.name === 'plus') {
        this.input.stepUp();
      } else {
        this.input.stepDown();
      }
      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(
          new CustomEvent('change', {
            bubbles: true,
            detail: event.target.name,
          })
        );
      }
    }
  }
);

// Checkout Button
customElements.define(
  'cart-checkout-button',
  class CartCheckoutButton extends HTMLElement {
    constructor() {
      super();

      this.querySelectorAll('[data-checkout-cart-drawer]').forEach((button) =>
        button.addEventListener('click', this.redirectToCheckout.bind(this))
      );
    }

    redirectToCheckout(event) {
      const elementGet = document.getElementById('items');
      if (!elementGet) return;

      const dataItems = elementGet.getAttribute('data-items');
      if (!dataItems) return;

      const parseData = JSON.parse(dataItems);
      const items = [];

      for (let i = 0; i < parseData.length; i++) {
        items.push({
          currency: 'EUR',
          item_name: parseData[i].title.replace(/\s+/g, '_').toLowerCase(),
          index: i + 1,
          item_brand: parseData[i].vendor.replace(/\s+/g, '_').toLowerCase(),
          item_list_name: parseData[i].handle.replace('-', '_').toLowerCase(),
          item_variant: parseData[i].variant_id,
          price: parseData[i].price,
          item_id: parseData[i].variant_id,
          quantity: parseData[i].quantity,
        });
      }

      const dataBeginCheckout = {
        event: 'begin_checkout',
        ecommerce: {
          items,
        },
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(dataBeginCheckout);

      const uriShop = Shopify.shop;
      fetch('/cart.js')
        .then((response) => response.json())
        .then((cart) => {
          const token = cart.token;
          const hasSubscription = cart.items.some((item) => {
            return (
              item.properties &&
              (item.properties.shipping_interval_frequency || item.properties.shipping_interval_unit_type)
            );
          });

          let urlCheckout;
          if (hasSubscription) {
            urlCheckout = `https://checkout.rechargeapps.com/r/checkout?myshopify_domain=${uriShop}&cart_token=${token}`;
          } else {
            urlCheckout = `${Shopify.routes.root}/cart/checkout`;
          }

          window.location.href = urlCheckout;
          setTimeout(() => {
            const loader = document.querySelector('#sideCartButton .loader');
            if (loader) loader.remove();
          }, 5000);
        });
    }
  }
);

// Cart Notification
customElements.define(
  'cart-notification',
  class CartNotification extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        cartCount: '[data-cart-count]',
      };
      this.cartNotification = this.querySelector(this.selectors.cartCount);
    }

    updateItemsCount(itemsCount) {
      if (!this.cartNotification) return;

      this.cartNotification.innerHTML = itemsCount;
    }

    getItemsCount() {
      if (!this.cartNotification) return;
      return this.cartNotification.innerHTML;
    }
  }
);

// Cart Drawer
customElements.define(
  'cart-drawer-z',
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        cartOpen: '[data-cart-drawer-open]',
        cartClose: '[data-cart-drawer-close]',
        cartDrawer: 'cart-drawer-v2',
        cartOverlay: 'cart-overlay',
        cartNotification: 'cart-notification',
        cartUpsellDynamic: 'cart-upsell-dynamic',
      };

      this.cartDrawer = document.getElementById(this.selectors.cartDrawer);
      this.cartOverlay = document.getElementById(this.selectors.cartOverlay);
      this.cartNotifications = document.querySelectorAll(this.selectors.cartNotification);

      this.cartDrawer?.addEventListener('keyup', (evt) => {
        if (evt.code === 'Escape') this.close();
      });

      document
        .querySelectorAll(this.selectors.cartOpen)
        .forEach((cartOpen) =>
          cartOpen.addEventListener('click', this.open.bind(this), this.handleCartUpdates.bind(this), false)
        );

      document
        .querySelectorAll(this.selectors.cartClose)
        .forEach((cartClose) => cartClose.addEventListener('click', this.close.bind(this)));

      this.init();
    }

    init() {
      // Detect cart interactions
      this.httpInterceptor(
        ['/cart/add.js', '/cart/add', '/cart/change', '/cart/update', '/cart/clear'],
        this.handleCartUpdates.bind(this)
      );
    }

    open() {
      this.cartDrawer?.classList.add('animate', 'active');
      this.cartOverlay?.classList.add('animate', 'active');
      document.body.style.overflow = 'hidden';
      this.initCheckoutButton();
    }

    close() {
      this.cartDrawer?.classList.remove('active');
      this.cartOverlay?.classList.remove('active');
      document.body.style.overflow = 'scroll';

      if (window.location.href.includes('/cart')) {
        window.location.href = '/';
      }
    }

    getSectionsToRender() {
      return [
        {
          id: 'main-cart-items',
          section: document.getElementById('main-cart-items')?.dataset.id || null,
          selector: '[data-items-cart]',
        },
        {
          id: 'cart-drawer-reward',
          section: document.getElementById('cart-drawer-reward')?.dataset.id || null,
          selector: '[data-cart-drawer-reward]',
        },
        {
          id: 'cart-drawer-samples',
          section: document.getElementById('cart-drawer-samples')?.dataset.id || null,
          selector: '[data-cart-drawer-samples]',
        },
        {
          id: 'main-cart-summary',
          section: document.getElementById('main-cart-summary')?.dataset.id || null,
          selector: '.cart-drawer-summary',
        },
        {
          id: 'drawer-cart-footer',
          section: document.getElementById('drawer-cart-footer')?.dataset.id || null,
          selector: '[data-cart-total-price]',
        },
        {
          id: 'drawer-cart-upsell-dynamic',
          section: document.getElementById('drawer-cart-upsell-dynamic')?.dataset.id || null,
          selector: '[data-upsell-dynamic-cart-drawer]',
        },
        {
          id: 'drawer-cart-upsell',
          section: document.getElementById('drawer-cart-upsell')?.dataset.id || null,
          selector: '[data-upsell-cart-drawer]',
        },
      ];
    }

    getSectionInnerHTML(html, selector) {
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const element = doc.querySelector(selector);
        if (!element) {
          throw new Error(`Element corresponding to selector "${selector}" not found.`);
        }
        return element.innerHTML;
      } catch (error) {
        console.error('Error parsing HTML or retrieving element:', error);
        return '';
      }
    }


    async renderContents() {
      await fetch(`${window.location.pathname}?sections=${this.cartDrawer.dataset.section}`)
        .then((response) => response.json())
        .then((response) => {
          this.getSectionsToRender().forEach((section) => {
            if (section.section != null) {
              const elementToReplace =
                document.getElementById(section.id)?.querySelector(section.selector) ||
                document.getElementById(section.id);
              if (elementToReplace != null) {
                elementToReplace.innerHTML = this.getSectionInnerHTML(
                  response[`${this.cartDrawer.dataset.section}`],
                  section.selector
                );
              }
            }
          });

          const dom = new DOMParser().parseFromString(response['cart-drawer-v2'], 'text/html');
          const mainCartItems = dom?.querySelector('#main-cart-items');
          if (mainCartItems) {
            const cartItemsElement = document.querySelector('#main-cart-items');
            if (cartItemsElement) {
              cartItemsElement.dataset.useSecurityCheckZax = mainCartItems.dataset.useSecurityCheckZax;
            }
          }

          // Initialize other components if necessary
          // For example:
          // this.initProgressBar();
          // this.initUpsellDynamic();
        })
        .catch((error) => {
          console.error('Error:', error);
        });

      this.open();
      this.initCheckoutButton();
      this.initProgressBars();
    }

    initProgressBars() {
      const rewardsProgressBar = document.querySelector('#cart-progressbar-rewards');
      if (rewardsProgressBar && typeof rewardsProgressBar.init === 'function') {
        rewardsProgressBar.init();
      }

      const cartUpsellDynamic = document.querySelector('cart-upsell-dynamic');
      if (cartUpsellDynamic && typeof cartUpsellDynamic.init === 'function') {
        cartUpsellDynamic.init();
      }
    }

    initCheckoutButton() {
      const checkoutButton = this.cartDrawer?.querySelector('[data-checkout-cart-drawer]');
      if (checkoutButton) {
        checkoutButton.addEventListener('click', () => {
          window.location.href = `${Shopify.routes.root}checkout`;
        });
      }
    }

    async handleCartUpdates(res, url, requestBody) {
      if (res?.preventDefault) {
        res.preventDefault();
      }

      // Detect if this is a non-cart update (e.g., from Gorgias, y_sess_id)
      // Skip opening the drawer if the request body contains no cart-related keys
      const isCartUpdate = url && url.includes('/cart/update');
      const isNonCartUpdate =
        isCartUpdate &&
        requestBody &&
        typeof requestBody === 'object' &&
        !requestBody.updates &&
        !requestBody.line &&
        !requestBody.items;

      const shopifyCart = await cartManager.getCart();
      this.cartNotifications.forEach((section) => {
        section.updateItemsCount(shopifyCart.item_count);
      });

      if (shopifyCart.item_count === 0) {
        this.classList.add('cart-empty');
      } else {
        this.classList.remove('cart-empty');
      }

      let refreshContentsAllow = true;

      if (localStorage.getItem('noteUpdated')) {
        localStorage.removeItem('noteUpdated');
        refreshContentsAllow = false;
      }
      if (localStorage.getItem('productUpdated')) {
        localStorage.removeItem('productUpdated');
        refreshContentsAllow = true;
      }
      if (localStorage.getItem('upsellAdded')) {
        localStorage.removeItem('upsellAdded');
        refreshContentsAllow = false;
      }
      if (localStorage.getItem('rewardAdded')) {
        localStorage.removeItem('rewardAdded');
        refreshContentsAllow = false;
      }
      if (localStorage.getItem('samplesAdded')) {
        localStorage.removeItem('samplesAdded');
        refreshContentsAllow = false;
      }

      if (url && (url.includes('change.js?drawer-item-reward') || url.includes('_easygift_internal=true'))) {
        refreshContentsAllow = false;
      }

      // Don't open cart drawer for non-cart updates (e.g., y_sess_id)
      if (isNonCartUpdate) {
        refreshContentsAllow = false;
      }

      if (refreshContentsAllow) {
        await this.renderContents();
      }

      /* EVENTS */
      /***********************/
      if (url && typeof url === 'string') {
        var requestVerb = new URL(url).pathname.replace(/(\/cart\/)([\D]*)(\.js)?/, function (match, p1, p2, p3) {
          return p2;
        });

        const updateEvent = new CustomEvent('cart:update', {
          detail: {
            response: res,
            request: requestVerb,
            items_count: shopifyCart.item_count,
            cart: shopifyCart,
          },
        });

        document.body.dispatchEvent(updateEvent);
      }

      // Dispatch custom events if necessary
      // Implement event handling logic here
    }

    httpInterceptor(urlsToMatch, callback) {
      const responseMatchesAnyUrl = (requestUrl, urls) => {
        return urls.some((url) => requestUrl.includes(url));
      };

      // Intercept fetch
      const originalFetch = window.fetch;
      window.fetch = function (...args) {
        // Capture request body if present
        let requestBody = null;
        if (args[1] && args[1].body) {
          try {
            requestBody = JSON.parse(args[1].body);
          } catch (e) {
            // Body is not JSON, ignore
          }
        }

        return originalFetch.apply(this, args).then(async (response) => {
          if (responseMatchesAnyUrl(response.url, urlsToMatch) && response.type !== 'cors' && response.ok) {
            const json = await response.clone().json();
            if (typeof callback === 'function') {
              await callback(json, response.url, requestBody);
            }
          }
          return response;
        });
      };

      // Intercept XMLHttpRequest
      const originalSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function (...args) {
        // Capture request body if present
        let requestBody = null;
        if (args[0]) {
          try {
            requestBody = JSON.parse(args[0]);
          } catch (e) {
            // Body is not JSON, ignore
          }
        }

        this.addEventListener(
          'readystatechange',
          async function () {
            if (responseMatchesAnyUrl(this.responseURL, urlsToMatch) && this.readyState === 4 && this.status === 200) {
              if (typeof callback === 'function') {
                await callback(JSON.parse(this.response), this.responseURL, requestBody);
              }
            }
          },
          false
        );
        originalSend.apply(this, args);
      };
    }
  }
);
